import React, { useState } from 'react';
import { Progress } from 'reactstrap';
import { NeoCard, NeoBadge } from '../../../../../components/Common';
import NeoLoader from '../../../../../components/Common/NeoLoader';
import moment from 'moment';
import { useKioskGamification } from '../../../../Kiosk/hooks/useKioskGamification';
import MemberEvaluationsPrintModal from './MemberEvaluationsPrintModal';

const ProfileEvaluation = ({ member }) => {
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

    // Reutilizando o hook do Kiosk, que já traz latestEvaluation e groupedCriteria formatadinhos
    const {
        latestEvaluation,
        allEvaluations,
        groupedTopics,
        loadingEval
    } = useKioskGamification(member);

    if (loadingEval) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                <NeoLoader type="inline" loading={loadingEval} />
            </div>
        );
    }

    if (!latestEvaluation) {
        return (
            <NeoCard thinBorder={true} className="mt-4" bodyClassName="d-flex flex-column align-items-center justify-content-center text-muted text-center p-5" bodyStyle={{ minHeight: "200px" }}>
                <h5 className="fw-bold text-dark">Nenhuma Avaliação</h5>
                <p className="mb-0">Não encontramos avaliações recentes registradas para este aluno.</p>
            </NeoCard>
        );
    }

    return (
        <React.Fragment>
            <NeoCard thinBorder={true} className="mt-4" bodyClassName="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h5 className="mb-0 fw-bold text-dark">{latestEvaluation.eventName || "Avaliação Técnica"}</h5>
                        <p className="text-muted mb-0 small">
                            <i className="mdi mdi-calendar-blank me-1"></i>
                            {moment(latestEvaluation.date).format("DD/MM/YYYY")}
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            onClick={() => setIsPrintModalOpen(true)}
                            className="btn btn-outline-primary d-flex align-items-center gap-2 fw-semibold px-3"
                            style={{ 
                                borderRadius: "30px", 
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                height: "34px"
                            }}
                        >
                            <i className="mdi mdi-printer" /> Imprimir Relatório
                        </button>
                        <NeoBadge 
                            color={latestEvaluation.status === "PASSED" ? "success" : "warning"}
                            icon={latestEvaluation.status === "PASSED" ? "mdi-check-circle" : "mdi-clock-outline"}
                            className="px-3 py-2 font-size-12"
                        >
                            {latestEvaluation.status === "PASSED" ? "Aprovado" : "Em Progresso"}
                        </NeoBadge>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold text-dark">Progresso para o próximo nível</span>
                        <span className="fw-bold text-primary">{latestEvaluation.progressPercent || 0}%</span>
                    </div>
                    <Progress value={latestEvaluation.progressPercent || 0} color="primary" style={{ height: "10px", backgroundColor: "rgba(0,0,0,0.1)" }} />
                </div>

                {Object.keys(groupedTopics).length > 0 && (
                    <div className="mt-4">
                        <h6 className="fw-bold text-secondary mb-3 text-uppercase" style={{ fontSize: "0.85rem", letterSpacing: "1px" }}>Tópicos Avaliados</h6>
                        <div className="table-responsive">
                            <table className="table table-borderless mb-0 bg-transparent">
                                <tbody>
                                    {Object.entries(groupedTopics).map(([objectiveName, topicsList], objIdx) => (
                                        <React.Fragment key={objIdx}>
                                            <tr className="border-0">
                                                <td colSpan="2" className="pt-4 pb-2 px-0 bg-transparent">
                                                    <div className="fw-bold mb-0" style={{ borderLeft: "4px solid var(--color-brand-accent)", paddingLeft: "10px", fontSize: "1.1rem", color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                                                        {objectiveName}
                                                    </div>
                                                </td>
                                            </tr>
                                            {topicsList.map((topic, idx) => {
                                                const uniqueId = `topic-${objIdx}-${idx}`;
                                                return (
                                                    <tr key={uniqueId} style={{ borderBottom: "1px solid rgba(72, 39, 18, 0.08)" }}>
                                                        <td className="py-2 px-0 bg-transparent align-middle" style={{ width: '60%' }}>
                                                            <div className="d-flex align-items-center">
                                                                 <div 
                                                                    className="fw-semibold text-dark text-truncate d-inline-block"
                                                                    style={{ maxWidth: topic.isFundamental ? 'calc(100% - 40px)' : '100%', color: 'var(--color-brand-primary)' }}
                                                                    title={topic.name}
                                                                 >
                                                                    {topic.name}
                                                                 </div>
                                                                 {topic.isFundamental && (
                                                                    <NeoBadge color="danger" className="font-size-10 ms-2" title="Fundamental">Fund.</NeoBadge>
                                                                 )}
                                                            </div>
                                                        </td>
                                                        <td className="py-2 text-end px-0 align-middle bg-transparent" style={{ width: '40%' }}>
                                                            <span 
                                                                className="fw-bold px-3 py-1 rounded-pill text-dark border text-truncate d-inline-block" 
                                                                style={{ 
                                                                    maxWidth: '100%', 
                                                                    borderRadius: '20px',
                                                                    border: '1.5px solid rgba(72, 39, 18, 0.15)',
                                                                    backgroundColor: 'var(--color-bg-canvas)',
                                                                    fontFamily: 'Outfit, sans-serif',
                                                                    fontSize: '12px',
                                                                    color: 'var(--color-brand-primary)'
                                                                }}
                                                            >
                                                                {topic.levelName || "Avaliado"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {latestEvaluation.observations && (
                    <div 
                        className="mt-4 p-3 rounded-3 text-muted border" 
                        style={{ 
                            backgroundColor: 'var(--color-bg-canvas)',
                            borderRadius: '12px',
                            borderColor: 'rgba(72, 39, 18, 0.15)',
                            color: 'var(--color-brand-primary)',
                            fontFamily: 'Outfit, sans-serif'
                        }}
                    >
                        <strong className="d-block mb-1" style={{ color: 'var(--color-brand-primary)' }}>Observações:</strong>
                        {latestEvaluation.observations}
                    </div>
                )}
            </NeoCard>

            <MemberEvaluationsPrintModal
                isOpen={isPrintModalOpen}
                toggle={() => setIsPrintModalOpen(!isPrintModalOpen)}
                member={member}
                allEvaluations={allEvaluations}
            />
        </React.Fragment>
    );
};

export default ProfileEvaluation;
